import { Text, TextLink } from '@moonpig/launchpad-components'
import { getDepartmentTitle } from '@moonpig/web-core-utils'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import React, { FC } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import { useStoreId } from '@moonpig/web-core-stores'
import { generateDepartmentHeadersClickGAEvent } from '../../analytics/GAEvents'
import type { AlternativeDepartmentDetails } from '../../types'
import { capitaliseFirstCharacter } from '../../utils'
import { useFindLocaleText } from '../../text-localisation'
import type { SearchContext } from '../../store/slices/shared'

type AltDepartmentResultsHeadersProps = {
  searchContext: SearchContext
  isZeroResults?: boolean
}

const StyledLink = styled(TextLink)`
  font-weight: bold;
  text-decoration: underline;
`

const getDepartmentHeaderLinkText = (departmentName: string, count: number) =>
  `${capitaliseFirstCharacter(departmentName)} (${count})`

export const AlternativeDepartmentResultsHeaders: FC<
  AltDepartmentResultsHeadersProps
> = ({ searchContext, isZeroResults }) => {
  const storeId = useStoreId()
  const localiseText = useFindLocaleText()
  const { selectedSuggestion, searchTerm } = searchContext
  const suggestedTerm = selectedSuggestion?.label || '...'

  const departmentsDetailsWithResults = searchContext.departmentCounts?.filter(
    departmentDetails =>
      departmentDetails.count > 0 &&
      departmentDetails.department !== searchContext.departments[0],
  )

  const viewText = localiseText('find.view_in', { isZeroResults })

  const getLinkUrl = (departmentDetails: AlternativeDepartmentDetails) => {
    let linkUrl = `/${storeId}/search/?`

    if (selectedSuggestion) {
      linkUrl += `suggestion=${selectedSuggestion.group}%3A${selectedSuggestion.facetKey}%3A${selectedSuggestion.label}`
    } else if (searchTerm !== '') {
      linkUrl += `q=${searchTerm}`
    }

    linkUrl += `&d=${departmentDetails.department.toLowerCase()}`
    return linkUrl
  }

  return (
    <>
      {departmentsDetailsWithResults
        ?.sort((a, b) => b.count - a.count)
        .map((departmentDetails, index) => {
          const departmentTitle = getDepartmentTitle(
            departmentDetails.department,
          )

          const departmentName = capitaliseFirstCharacter(departmentTitle)

          const localisedDepartmentName = capitaliseFirstCharacter(
            localiseText('find.department', {
              departmentName: departmentTitle,
            }),
          )

          const departmentLinkText = getDepartmentHeaderLinkText(
            localisedDepartmentName,
            departmentDetails.count,
          )

          return (
            <Text
              key={departmentDetails.department}
              lineHeight={{ xs: 2, md: 1.5 }}
            >
              {index > 0 ? localiseText('find.or_in') : ` ${viewText}`}
              {
                <StyledLink
                  href={getLinkUrl(departmentDetails)}
                  onClick={() =>
                    trackGAEvent(
                      generateDepartmentHeadersClickGAEvent(
                        searchTerm || suggestedTerm,
                        departmentName,
                        departmentDetails.count,
                      ),
                    )
                  }
                >
                  {departmentLinkText}
                </StyledLink>
              }
              &nbsp;
            </Text>
          )
        })}
    </>
  )
}
